import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Switch,
  Autocomplete,
  Tooltip,
  Button,
  Paper,
  Collapse,
  DialogContentText
} from '@mui/material';
import { format, parseISO, isBefore, addMinutes, differenceInMinutes, isToday, addDays } from 'date-fns';
import { useAppointmentBooking } from '../hooks/useAppointmentBooking';
import { doctorScheduleApi } from '../api/doctorScheduleApi';
import { 
  PatientDto, 
  EmployeeDto, 
  LocationDto, 
  Appointment, 
  AppointmentStatus, 
  AppointmentType,
  AppointmentFormData 
} from '../types/appointment.types';
import { useSnackbar } from 'notistack';
import { 
  Schedule as ScheduleIcon,
  ArrowBackIos,
  ArrowForwardIos,
  Today as TodayIcon
} from '@mui/icons-material';

// Utils
import { formatDateForBackend, formatTime } from '../../../utils/dateUtils';
import { PatientService } from '../../patients/services/patient.service';
import { patientApi } from '../../patients/api/patientApi';

interface AppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  appointment?: Appointment;
  mode: 'create' | 'edit' | 'view' | 'delete';
  onSave: (appointment: Partial<Appointment>) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onRefresh?: () => void;
  patients?: PatientDto[];
  doctors?: EmployeeDto[];
  locations?: LocationDto[];
  loading?: boolean;
}

interface NonRegisteredPatientData {
  fullName: string;
  mobile: string;
  email: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER' | '';
  dob: string;
  isFamily?: boolean;
  familyId?: string;
}

type FormData = {
  id?: string;
  patientId: string;
  doctorId: string;
  locationId: string;
  appointmentType: AppointmentType;
  date: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  notes: string;
  isNonRegisteredPatient: boolean;
  nonRegisteredPatient: NonRegisteredPatientData;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  reason: string;
};

interface TimeSlot {
  time: string;
  available: boolean;
  isSelected?: boolean;
  isPast?: boolean;
  bookedPatient?: string | null;
  appointmentId?: string | null;
}

const priorityOptions: { value: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

const typeOptions: { value: AppointmentType; label: string }[] = [
  { value: 'CONSULTATION', label: 'Consultation' },
  { value: 'FOLLOW_UP', label: 'Follow Up' },
  { value: 'THERAPIES', label: 'Therapies' },
  { value: 'OTC', label: 'OTC' },
  { value: 'WALK_IN', label: 'Walk In' },
  { value: 'OTHER', label: 'Other' },
];



// Helper functions for slot styling
const getSlotColor = (slot: TimeSlot) => {
  if (slot.isPast) return 'inherit';
  if (!slot.available) return 'error';
  return 'success';
};

const getSlotTooltip = (slot: TimeSlot) => {
  if (slot.bookedPatient) return `Booked for ${slot.bookedPatient}`;
  if (slot.isPast) return 'This time slot has already passed';
  if (!slot.available) return 'This time slot is not available';
  return 'Click to select this time slot';
};

const getSlotStyles = (slot: TimeSlot, isSelected: boolean) => {
  if (isSelected) {
    return {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': {
        bgcolor: 'primary.dark',
      },
    };
  }

  if (slot.isPast) {
    return {
      color: 'text.disabled',
      bgcolor: 'action.disabledBackground',
      borderColor: 'divider',
    };
  }

  if (!slot.available) {
    return {
      color: 'error.main',
      bgcolor: 'rgba(244, 67, 54, 0.08)',
      borderColor: 'error.light',
      '&:hover': {
        bgcolor: 'rgba(244, 67, 54, 0.12)',
      },
    };
  }

  return {
    color: 'success.dark',
    bgcolor: 'rgba(76, 175, 80, 0.1)',
    borderColor: 'success.light',
    '&:hover': {
      bgcolor: 'rgba(76, 175, 80, 0.15)',
    },
  };
};

const AppointmentDialog: React.FC<AppointmentDialogProps> = ({
  open,
  onClose,
  appointment: initialAppointment,
  mode,
  onSave,
  onDelete,
  onRefresh,
  patients = [],
  doctors = [],
  locations = [],
  loading = false
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [showSchedule, setShowSchedule] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [doctorSchedule, setDoctorSchedule] = useState<TimeSlot[]>([]);
  // Removed unused theme variable
  
  // Function to show error message
  
  // Form state
  const [formData, setFormData] = useState<FormData>({
    patientId: '',
    doctorId: '',
    locationId: '',
    appointmentType: 'CONSULTATION',
    date: formatDateForBackend(new Date()),
    startTime: formatTime(new Date()),
    endTime: formatTime(addMinutes(new Date(), 15)),
    status: AppointmentStatus.SCHEDULED,
    notes: '',
    isNonRegisteredPatient: false,
    nonRegisteredPatient: {
      fullName: '',
      mobile: '',
      email: '',
      gender: '' as const,
      dob: ''
    },
    priority: 'MEDIUM',
    reason: ''
  });
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize booking hook
  const { bookAppointment, isSubmitting: isBooking } = useAppointmentBooking({
    onSuccess: () => {
      // Refresh appointment data before closing dialog
      if (onRefresh) {
        onRefresh();
      }
      onClose();
      enqueueSnackbar('Appointment booked successfully!', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Booking error:', error);
      enqueueSnackbar(error.message || 'Failed to book appointment', { variant: 'error' });
    }
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientDto | null>(
    initialAppointment && patients?.find(p => p.id === initialAppointment.patientId) || null
  );
  const [isNonRegisteredPatient, setIsNonRegisteredPatient] = useState(false);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [searchedPatients, setSearchedPatients] = useState<PatientDto[]>([]);
  
  // Duplicate detection state
  const [duplicateDialog, setDuplicateDialog] = useState<{
    open: boolean;
    existingPatient?: {
      id: string;
      mrn: string;
      fullName: string;
      phoneNumber: string;
      age: string;
      gender: string;
      email: string;
    };
  }>({ open: false });
  
  const [nameConflictDialog, setNameConflictDialog] = useState<{
    open: boolean;
    conflictName?: string;
  }>({ open: false });
  
  const [phoneValidationTimeout, setPhoneValidationTimeout] = useState<number | null>(null);
  const [isValidatingPhone, setIsValidatingPhone] = useState(false);
  
  // Handle phone number validation with debounce
  const validatePhoneNumber = useCallback(async (phoneNumber: string) => {
    if (!phoneNumber || phoneNumber.length < 10) return;
    
    setIsValidatingPhone(true);
    try {
      const patients = await PatientService.checkPhoneExists(phoneNumber);
      if (patients && patients.length > 0) {
        // Find primary patient (isFamily=false)
        const primaryPatient = patients.find(p => !p.isFamily);
        if (primaryPatient) {
          setDuplicateDialog({
            open: true,
            existingPatient: {
              id: primaryPatient.id,
              mrn: primaryPatient.mrn,
              fullName: primaryPatient.fullName,
              phoneNumber: primaryPatient.phoneNumber || phoneNumber,
              age: primaryPatient.age?.toString() || 'N/A',
              gender: primaryPatient.gender || 'N/A',
              email: primaryPatient.email || 'N/A',
            }
          });
        }
      }
    } catch (error) {
      console.error('Error checking phone number:', error);
    } finally {
      setIsValidatingPhone(false);
    }
  }, []);

  const handlePhoneNumberChange = (value: string) => {
    setNonRegisteredPatientInfo(prev => ({
      ...prev,
      mobile: value
    }));

    // Clear existing timeout
    if (phoneValidationTimeout) {
      clearTimeout(phoneValidationTimeout);
    }

    // Set new timeout for validation
    if (value.length >= 10) {
      const timeoutId = window.setTimeout(() => {
        validatePhoneNumber(value);
      }, 500);
      setPhoneValidationTimeout(timeoutId);
    }
  };

  // Handle booking for existing patient (no family linking)
  const handleBookForExistingPatient = () => {
    if (duplicateDialog.existingPatient) {
      // Update form data with the existing patient ID
      setFormData(prev => ({
        ...prev,
        isNonRegisteredPatient: false,
        patientId: duplicateDialog.existingPatient!.id,
        nonRegisteredPatient: {
          fullName: '',
          mobile: '',
          email: '',
          gender: '' as const,
          dob: ''
        }
      }));
      
      // Create a patient object from the existing patient data and set it as selected
      const existingPatientForSelection: PatientDto = {
        id: duplicateDialog.existingPatient!.id,
        fullName: duplicateDialog.existingPatient!.fullName,
        mrn: duplicateDialog.existingPatient!.mrn,
        phone: duplicateDialog.existingPatient!.phoneNumber,
        age: typeof duplicateDialog.existingPatient!.age === 'string' 
          ? parseInt(duplicateDialog.existingPatient!.age) || 0 
          : duplicateDialog.existingPatient!.age || 0,
        gender: duplicateDialog.existingPatient!.gender,
        email: duplicateDialog.existingPatient!.email || '',
        address: '', // Default empty address
        isActive: true // Default to active
      };
      
      setSelectedPatient(existingPatientForSelection);
      setPatientSearch(duplicateDialog.existingPatient!.fullName);
      
      console.log('Patient selected for appointment:', existingPatientForSelection);
      console.log('Patient search set to:', duplicateDialog.existingPatient!.fullName);
      
      // Switch off non-registered mode
      setIsNonRegisteredPatient(false);
      setNonRegisteredPatientInfo({
        fullName: '',
        mobile: '',
        email: '',
        gender: '' as const,
        dob: ''
      });
      
      console.log(`Appointment will be booked for existing patient: ${duplicateDialog.existingPatient.fullName} (${duplicateDialog.existingPatient.mrn})`);
    }
    
    setDuplicateDialog({ open: false });
  };

  // Handle linking as family member - prepare data but don't create patient yet
  const handleLinkAsFamily = () => {
    if (duplicateDialog.existingPatient && nonRegisteredPatientInfo.fullName.trim()) {
      // Prepare family member data but don't create patient yet
      // This will be handled during the appointment booking process
      setFormData(prev => ({
        ...prev,
        isNonRegisteredPatient: true, // Keep as non-registered but with family link info
        patientId: '', // Clear patient ID since we'll create the patient during booking
        nonRegisteredPatient: {
          ...nonRegisteredPatientInfo,
          phoneNumber: duplicateDialog.existingPatient!.phoneNumber,
          // Add family linking information
          familyId: duplicateDialog.existingPatient!.id,
          isFamily: true, // Mark as family member
        }
      }));
      
      // Also update the local state to reflect family linking
      setNonRegisteredPatientInfo(prev => ({
        ...prev,
        // Store family link info for later use
        familyId: duplicateDialog.existingPatient!.id,
        isFamily: true, // Mark as family member
      }));
      
      console.log(`Family member data prepared for: ${nonRegisteredPatientInfo.fullName} linked to ${duplicateDialog.existingPatient!.fullName}`);
      console.log('Patient will be created during appointment booking process');
    }
    
    setDuplicateDialog({ open: false });
  };

  // Handle using different phone number
  const handleUseDifferentNumber = () => {
    setDuplicateDialog({ open: false });
    // Clear the phone number field
    setNonRegisteredPatientInfo(prev => ({
      ...prev,
      mobile: ''
    }));
  };
  

  // State for non-registered patient info
  const [nonRegisteredPatientInfo, setNonRegisteredPatientInfo] = useState<NonRegisteredPatientData>({
    fullName: '',
    mobile: '',
    email: '',
    gender: '' as const,
    dob: ''
  });
  
  // Update form data when non-registered patient info changes
  useEffect(() => {
    if (isNonRegisteredPatient) {
      setFormData(prev => ({
        ...prev,
        nonRegisteredPatient: {
          ...prev.nonRegisteredPatient,
          ...nonRegisteredPatientInfo,
          phoneNumber: nonRegisteredPatientInfo.mobile
        }
      }));
    }
  }, [nonRegisteredPatientInfo, isNonRegisteredPatient]);

  const handleNonRegisteredPatientToggle = (checked: boolean) => {
    // Update local state
    setIsNonRegisteredPatient(checked);
    
    // Reset non-registered patient info
    setNonRegisteredPatientInfo({
      fullName: '',
      mobile: '',
      email: '',
      gender: '' as const,
      dob: ''
    });
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      isNonRegisteredPatient: checked,
      patientId: '', // Always clear patientId when toggling
      nonRegisteredPatient: {
        fullName: '',
        mobile: '',
        email: '',
        gender: '' as const,
        dob: ''
      }
    }));
    
    // Clear any patient selection
    setSelectedPatient(null);
    setPatientSearch('');
  };

  // Mode helpers
  const isViewMode = mode === 'view';
  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';
  const isDeleteMode = mode === 'delete';

  // Initialize form when opening or when appointment changes
  useEffect(() => {
    if (open) {
      setErrors({});
      setShowSchedule(false); // Collapse schedule by default
      setIsNonRegisteredPatient(false); // Reset toggle state
      
      if (initialAppointment) {
        // Editing existing appointment
        // Destructure only what we need from initialAppointment
        
        // Create a new form data object with only the fields we need
        const initialFormData: FormData = {
          patientId: initialAppointment?.patientId || '',
          doctorId: initialAppointment?.doctorId || '',
          locationId: initialAppointment?.locationId || '',
          appointmentType: initialAppointment?.type || 'CONSULTATION',
          date: initialAppointment ? format(parseISO(initialAppointment.startTime), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
          startTime: initialAppointment ? format(parseISO(initialAppointment.startTime), 'HH:mm') : '',
          endTime: initialAppointment ? format(parseISO(initialAppointment.endTime), 'HH:mm') : '',
          status: initialAppointment?.status || 'SCHEDULED',
          notes: initialAppointment?.notes || '',
          isNonRegisteredPatient: false,
          nonRegisteredPatient: {
            fullName: '',
            mobile: '',
            email: '',
            gender: '',
            dob: ''
          },
          priority: initialAppointment.priority,
          reason: initialAppointment.reason || ''
        };
        
        setFormData(initialFormData);
        
        if (initialAppointment.patient) {
          setSelectedPatient(initialAppointment.patient);
          setPatientSearch(initialAppointment.patient.fullName || '');
        } else {
          setSelectedPatient(null);
          setPatientSearch('');
        }
      } else {
        // New appointment
        setFormData({
          patientId: '',
          doctorId: '',
          locationId: '',
          appointmentType: 'CONSULTATION',
          date: formatDateForBackend(new Date()),
          startTime: formatTime(new Date()),
          endTime: formatTime(addMinutes(new Date(), 15)),
          status: AppointmentStatus.SCHEDULED,
          notes: '',
          isNonRegisteredPatient: false,
          nonRegisteredPatient: {
            fullName: '',
            mobile: '',
            email: '',
            gender: '',
            dob: ''
          },
          priority: 'MEDIUM' as const,
          reason: ''
        });
        setSelectedPatient(null);
        setPatientSearch('');
      }
    }
  }, [open, initialAppointment]);

  // Parse time string to Date
  const parseTime = useCallback((timeString: string): Date => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }, []);

  // Combine date and time into a single Date object
  const combineDateTime = useCallback((date: Date | null, time: Date | null): Date => {
    if (!date || !time) return new Date();
    
    const newDate = new Date(date);
    newDate.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return newDate;
  }, []);

  // Handle form field changes
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (!name) return;
    
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, [errors]);

  // Handle select changes
  const handleSelectChange = useCallback((e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (!name) return;
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Handle patient selection from autocomplete

  // Handle date change
  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      date: value,
    }));
    
    // Update selectedDate to trigger doctor schedule reload
    if (value) {
      setSelectedDate(new Date(value));
    }
    
    // Clear any date-related errors
    if (errors.date) {
      setErrors(prev => ({
        ...prev,
        date: '',
      }));
    }
  }, [errors.date]);

  // Handle time change
  const handleTimeChange = useCallback((field: 'startTime' | 'endTime', time: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: time,
    }));
    
    // Clear any time-related errors
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  }, [errors]);

  // Validate form
  const validateForm = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    
    // Skip patient validation in 'Save & Book' mode (non-registered patient flow)
    if (!formData.isNonRegisteredPatient && !formData.patientId) {
      newErrors.patientId = 'Patient is required';
    }
    
    if (!formData.doctorId) {
      newErrors.doctorId = 'Doctor is required';
    }
    
    if (!formData.locationId) {
      newErrors.locationId = 'Location is required';
    }
    
    if (!formData.date) {
      newErrors.date = 'Appointment date is required';
    } else {
      // Check if the selected date is in the past
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      
      if (selectedDate < today) {
        newErrors.date = 'Cannot book appointments for past dates';
      }
    }
    
    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }
    
    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    } else if (formData.startTime && formData.endTime) {
      const start = new Date(`${formData.date}T${formData.startTime}`);
      const end = new Date(`${formData.date}T${formData.endTime}`);
      
      if (end <= start) {
        newErrors.endTime = 'End time must be after start time';
      }
    }
    
    return newErrors;
  }, [formData]);

  // Handle form submission
  // Check if a time slot is currently selected
  const isTimeSelected = (time: string) => {
    return formData.startTime === time;
  };

  // Handle time slot selection
  const handleTimeSlotSelect = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const slotDate = new Date(selectedDate);
    slotDate.setHours(hours, minutes, 0, 0);
    
    setFormData(prev => ({
      ...prev,
      appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
      startTime: time,
      endTime: format(addMinutes(slotDate, 15), 'HH:mm')
    }));
  };

  // Load doctor's schedule when doctor or date changes
  useEffect(() => {
    const loadDoctorSchedule = async () => {
      if (!formData.doctorId || !showSchedule) return;
      
      setIsLoadingSchedule(true);
      try {
        const formattedDate = format(selectedDate, 'yyyy-MM-dd');
        const response = await doctorScheduleApi.getDoctorSchedule({
          doctorId: formData.doctorId,
          date: formattedDate,
        });
        
        // Map the API response to our TimeSlot interface
        const mappedSlots = response.slots.map(slot => {
          const slotDateTime = new Date(`${formattedDate}T${slot.time}`);
          return {
            time: slot.time,
            available: slot.available,
            isSelected: isTimeSelected(slot.time),
            isPast: isBefore(slotDateTime, new Date()),
            bookedPatient: slot.patientName || null,
            appointmentId: slot.appointmentId || null
          };
        });
        
        setDoctorSchedule(mappedSlots);
      } catch (error) {
        console.error('Failed to load doctor schedule:', error);
        enqueueSnackbar('Failed to load doctor schedule', { variant: 'error' });
      } finally {
        setIsLoadingSchedule(false);
      }
    };
    
    loadDoctorSchedule();
  }, [formData.doctorId, selectedDate, showSchedule]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting || isBooking) return;
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Combine date and time for start and end times
      const startDateTime = combineDateTime(
        new Date(formData.date),
        parseTime(formData.startTime)
      );
      
      const endDateTime = combineDateTime(
        new Date(formData.date),
        parseTime(formData.endTime)
      );
      
      // Calculate duration in minutes
      const duration = differenceInMinutes(endDateTime, startDateTime);
      
      // Find selected patient and doctor for their names
      
      // Format time strings
      const timeStr = format(startDateTime, 'HH:mm');
      const endTimeStr = format(endDateTime, 'HH:mm');
      
      // Prepare appointment data matching AppointmentFormData type
      const appointmentData: Omit<AppointmentFormData, 'patientId'> & { patientId?: string } = {
        doctorId: formData.doctorId,
        locationId: formData.locationId,
        date: formData.date,  // Required by AppointmentFormData
        time: timeStr,        // Format as HH:mm
        endTime: endTimeStr,  // Format as HH:mm
        duration,             // In minutes
        type: formData.appointmentType,
        status: formData.status,
        reason: formData.reason,
        notes: formData.notes,
        isWalkIn: false,      // Default value
        priority: formData.priority,
        // Add patientId if this is for a registered patient
        ...(formData.patientId && { patientId: formData.patientId })
      };
      
      // Handle non-registered patient flow
      if (formData.isNonRegisteredPatient && formData.nonRegisteredPatient) {
        // Calculate DOB from age if not provided
        // Calculate age from DOB
        const calculateAgeFromDob = (dob: string): string => {
          if (!dob) return '';
          const dobParts = dob.split('-');
          const birthDate = new Date(parseInt(dobParts[0]), parseInt(dobParts[1]) - 1, parseInt(dobParts[2]));
          const today = new Date();
          const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          
          let years = todayLocal.getFullYear() - birthDate.getFullYear();
          let months = todayLocal.getMonth() - birthDate.getMonth();
          let days = todayLocal.getDate() - birthDate.getDate();
          
          if (days < 0) {
            months--;
            const lastMonth = new Date(todayLocal.getFullYear(), todayLocal.getMonth(), 0);
            days += lastMonth.getDate();
          }
          
          if (months < 0) {
            years--;
            months += 12;
          }
          
          // Format age based on the actual age
          if (years > 0) {
            return years === 1 ? '1 year' : `${years} years`;
          } else if (months > 0) {
            return months === 1 ? '1 month' : `${months} months`;
          } else if (days > 0) {
            return days === 1 ? '1 day' : `${days} days`;
          } else {
            return 'Newborn';
          }
        };

        const dob = formData.nonRegisteredPatient.dob;
        const age = dob ? calculateAgeFromDob(dob) : '';

        const nonRegisteredPatientData = {
          fullName: formData.nonRegisteredPatient.fullName,
          phoneNumber: formData.nonRegisteredPatient.mobile,
          email: formData.nonRegisteredPatient.email,
          gender: formData.nonRegisteredPatient.gender || 'OTHER',
          age: age,
          dob: dob,
          isActive: true,
          address: '',
          note: '',
          // Include family member information if present
          isFamily: formData.nonRegisteredPatient.isFamily || false,
          familyId: formData.nonRegisteredPatient.familyId || undefined
        };
        
        await bookAppointment(nonRegisteredPatientData, appointmentData, true);
      } 
      // Handle registered patient flow
      else if (formData.patientId) {
        await bookAppointment(null, { ...appointmentData, patientId: formData.patientId }, false);
      } else {
        throw new Error('Invalid form data');
      }
      
      // onClose will be called by the onSuccess callback from useAppointmentBooking
    } catch (error: any) {
      console.error('Error processing appointment:', error);
      
      // Extract error message from the error object
      let errorMessage = 'Failed to process appointment. Please try again.';
      
      // Handle different error object structures
      const responseData = error?.response?.data || {};
      
      // Log the validation errors if they exist
      if (error?.response?.status === 400 && responseData.errors) {
        console.log('Validation errors:', responseData.errors);
        // Update form errors with server-side validation errors
        const serverErrors: Record<string, string> = {};
        
        if (Array.isArray(responseData.errors)) {
          // Handle array of error messages
          responseData.errors.forEach((errorMsg: string) => {
            // Try to extract field name from error message
            const fieldMatch = errorMsg.match(/^(\w+)/);
            if (fieldMatch) {
              serverErrors[fieldMatch[1]] = errorMsg;
            } else {
              serverErrors.form = errorMsg;
            }
          });
        } else if (typeof responseData.errors === 'object') {
          // Handle object with field names as keys
          Object.entries(responseData.errors).forEach(([field, messages]) => {
            if (Array.isArray(messages) && messages.length > 0) {
              serverErrors[field] = messages[0];
            } else if (typeof messages === 'string') {
              serverErrors[field] = messages;
            }
          });
        } else if (typeof responseData.message === 'string') {
          serverErrors.form = responseData.message;
        }
        
        if (Object.keys(serverErrors).length > 0) {
          setErrors(serverErrors);
          return;
        }
      }
      
      // Get the message from the response data or error object
      errorMessage = responseData.message || error.message || errorMessage;
      
      // Clean up the error message if it includes the prefix
      if (errorMessage.startsWith('Failed to create appointment: ')) {
        errorMessage = errorMessage.replace('Failed to create appointment: ', '');
      }
      
      // Log the final error message
      console.log('Displaying error to user:', errorMessage);
      
      // Format the error message for better readability
      const formattedMessage = errorMessage
        .replace('Doctor is not available at the requested time. ', '')
        .replace('Already booked for', '\nAlready booked for');
      
      // Create and style the error container
      const errorDiv = document.createElement('div');
      Object.assign(errorDiv.style, {
        position: 'fixed',
        top: '20px',
        right: '20px',
        backgroundColor: '#fff',
        color: '#d32f2f',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        zIndex: '9999',
        maxWidth: '320px',
        borderLeft: '4px solid #d32f2f',
        fontFamily: 'Roboto, sans-serif',
        fontSize: '14px',
        lineHeight: '1.4'
      });

      // Add close button
      const closeBtn = document.createElement('span');
      closeBtn.innerHTML = '&times;';
      closeBtn.style.cssText = `
        position: absolute;
        top: 4px;
        right: 8px;
        cursor: pointer;
        font-size: 18px;
        color: #999;
      `;
      closeBtn.onclick = () => document.body.removeChild(errorDiv);

      // Add content
      errorDiv.innerHTML = `
        <div style="font-weight: 500; margin-bottom: 6px; display: flex; align-items: center; gap: 8px;">
          <span>⚠️</span>
          <span>Time Slot Unavailable</span>
        </div>
        <div style="color: #333; margin-bottom: 8px;">${formattedMessage}</div>
      `;
      
      errorDiv.appendChild(closeBtn);
      document.body.appendChild(errorDiv);
      
      // Auto-remove after 8 seconds
      setTimeout(() => {
        if (document.body.contains(errorDiv)) {
          document.body.removeChild(errorDiv);
        }
      }, 8000);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onSave, onClose, enqueueSnackbar, validateForm, combineDateTime, parseTime]);

  // Handle appointment deletion
  const handleDelete = useCallback(async () => {
    if (!initialAppointment?.id || !onDelete) return;
    
    try {
      setIsSubmitting(true);
      await onDelete(initialAppointment.id);
      onClose();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      enqueueSnackbar('Failed to delete appointment', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [initialAppointment, onDelete, onClose, enqueueSnackbar]);

  // Search patients from API when user types
  useEffect(() => {
    const searchPatientsFromApi = async () => {
      if (!patientSearch || patientSearch.trim().length < 2) {
        // If search is empty or too short, use the paginated list from props
        setSearchedPatients([]);
        setPatientsLoading(false);
        return;
      }

      try {
        setPatientsLoading(true);
        const results = await patientApi.searchPatients(patientSearch.trim());
        
        // Map Patient[] to PatientDto[] to match the expected type
        const mappedResults: PatientDto[] = results.map(patient => ({
          id: patient.id,
          mrn: patient.mrn,
          fullName: patient.fullName,
          age: patient.age ? (typeof patient.age === 'string' ? parseInt(patient.age) || 0 : patient.age) : 0,
          gender: patient.gender,
          phone: patient.phoneNumber || '',
          email: patient.email || '',
          address: patient.address || '',
          note: patient.note,
          isActive: patient.isActive
        }));
        
        setSearchedPatients(mappedResults);
      } catch (error) {
        console.error('Error searching patients:', error);
        enqueueSnackbar('Failed to search patients', { variant: 'error' });
        setSearchedPatients([]);
      } finally {
        setPatientsLoading(false);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(() => {
      searchPatientsFromApi();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [patientSearch, enqueueSnackbar]);

  // Use searched patients if available, otherwise use the paginated list from props
  const filteredPatients = useMemo(() => {
    // If user is actively searching (search term >= 2 chars), use search results
    if (patientSearch && patientSearch.trim().length >= 2) {
      // Filter out inactive patients from search results
      return searchedPatients.filter(patient => patient.isActive);
    }
    
    // Otherwise, use the paginated list from props (also filter inactive)
    return (patients || []).filter(patient => patient.isActive);
  }, [patientSearch, searchedPatients, patients]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ 
        sx: { 
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          '& .MuiDialogContent-root': {
            flex: '1 1 auto',
            overflow: 'auto'
          },
          '& .MuiDialogActions-root': {
            flex: '0 0 auto',
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            padding: 2,
            zIndex: 1
          }
        } 
      }}
    >
      <DialogTitle id="appointment-dialog-title">
        {isCreateMode && 'New Appointment'}
        {isEditMode && 'Edit Appointment'}
        {isViewMode && 'View Appointment'}
        {isDeleteMode && 'Delete Appointment'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ '& .MuiFormControl-root': { mb: 2 }, overflow: 'auto' }}>
          <Grid container spacing={1}>
            {/* Row 1: Non-registered patient toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isNonRegisteredPatient}
                    onChange={(e) => handleNonRegisteredPatientToggle(e.target.checked)}
                    color="primary"
                  />
                }
                label="Non-registered patient"
                labelPlacement="start"
                sx={{ 
                  marginLeft: 0,
                  '& .MuiFormControlLabel-label': {
                    marginLeft: 1
                  }
                }}
              />
            </Grid>

            {/* Row 2: Non-registered patient fields (shown when non-registered mode is on) */}
            {isNonRegisteredPatient && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={nonRegisteredPatientInfo.fullName}
                    onChange={(e) => 
                      setNonRegisteredPatientInfo(prev => ({
                        ...prev,
                        fullName: e.target.value
                      }))
                    }
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Mobile"
                    value={nonRegisteredPatientInfo.mobile}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, '');
                      handlePhoneNumberChange(value);
                    }}
                    onBlur={(e) => {
                      const phoneNumber = e.target.value.trim();
                      if (phoneNumber.length >= 10) {
                        validatePhoneNumber(phoneNumber);
                      }
                    }}
                    size="small"
                    required
                    inputProps={{ maxLength: 10 }}
                    InputProps={{
                      endAdornment: isValidatingPhone ? (
                        <CircularProgress size={20} />
                      ) : null,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={nonRegisteredPatientInfo.email}
                    onChange={(e) => 
                      setNonRegisteredPatientInfo(prev => ({
                        ...prev,
                        email: e.target.value
                      }))
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={nonRegisteredPatientInfo.dob}
                    onChange={(e) => {
                      setNonRegisteredPatientInfo(prev => ({
                        ...prev,
                        dob: e.target.value
                      }));
                    }}
                    size="small"
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={nonRegisteredPatientInfo.gender}
                      label="Gender"
                      onChange={(e) => 
                        setNonRegisteredPatientInfo(prev => ({
                          ...prev,
                          gender: e.target.value as 'MALE' | 'FEMALE' | 'OTHER' | ''
                        }))
                      }
                    >
                      <MenuItem value="MALE">Male</MenuItem>
                      <MenuItem value="FEMALE">Female</MenuItem>
                      <MenuItem value="OTHER">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Row 3: Patient, Doctor, Location, Type */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.patientId} size="small">
                <Autocomplete
                  options={filteredPatients}
                  getOptionLabel={(option) => 
                    option ? `${option.fullName} (${option.mrn || 'No MRN'})` : ''
                  }
                  value={selectedPatient}
                  onChange={(_, newValue) => {
                    setSelectedPatient(newValue);
                    setFormData(prev => ({
                      ...prev,
                      patientId: newValue?.id || ''
                    }));
                  }}
                  inputValue={patientSearch}
                  onInputChange={(_, newValue) => setPatientSearch(newValue)}
                  disabled={isViewMode || isDeleteMode || isNonRegisteredPatient}
                  loading={patientsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Patient"
                      variant="outlined"
                      required
                      error={!!errors.patientId}
                      helperText={errors.patientId}
                      size="small"
                      placeholder="Search patient"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {patientsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.doctorId} size="small">
                <InputLabel id="doctor-select-label">Doctor</InputLabel>
                <Select
                  labelId="doctor-select-label"
                  name="doctorId"
                  value={formData.doctorId}
                  onChange={handleSelectChange}
                  label="Doctor"
                  disabled={isViewMode || isDeleteMode}
                  required
                  error={!!errors.doctorId}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select a doctor</em>
                  </MenuItem>
                  {doctors?.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id}>
                      {doctor.fullName || `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim()}
                    </MenuItem>
                  ))}
                </Select>
                {errors.doctorId && (
                  <FormHelperText error>{errors.doctorId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.locationId} size="small">
                <InputLabel id="location-select-label">Location</InputLabel>
                <Select
                  labelId="location-select-label"
                  name="locationId"
                  value={formData.locationId}
                  onChange={handleSelectChange}
                  label="Location"
                  disabled={isViewMode || isDeleteMode}
                  required
                  error={!!errors.locationId}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select a location</em>
                  </MenuItem>
                  {locations?.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.locationId && (
                  <FormHelperText error>{errors.locationId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="type-select-label">Type</InputLabel>
                <Select
                  labelId="type-select-label"
                  name="appointmentType"
                  value={formData.appointmentType}
                  onChange={handleSelectChange}
                  label="Appointment Type"
                  disabled={isViewMode || isDeleteMode}
                  size="small"
                >
                  {typeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Row 2: Date, Times, and Priority */}
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: format(new Date(), 'yyyy-MM-dd'), // Block past dates
                }}
                disabled={isViewMode || isDeleteMode}
                error={!!errors.date}
                helperText={errors.date}
                required
                size="small"
              />
            </Grid>

            <Grid item xs={6} sm={4} md={2.5}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={(e) => handleTimeChange('startTime', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 300, // 5 min
                }}
                disabled={isViewMode || isDeleteMode}
                error={!!errors.startTime}
                helperText={errors.startTime}
                required
                size="small"
              />
            </Grid>

            <Grid item xs={6} sm={4} md={2.5}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={(e) => handleTimeChange('endTime', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 300, // 5 min
                }}
                disabled={isViewMode || isDeleteMode}
                error={!!errors.endTime}
                helperText={errors.endTime}
                required
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="priority-select-label">Priority</InputLabel>
                <Select
                  labelId="priority-select-label"
                  name="priority"
                  value={formData.priority}
                  onChange={handleSelectChange}
                  label="Priority"
                  disabled={isViewMode || isDeleteMode}
                  size="small"
                >
                  {priorityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Hidden status field to maintain form data */}
            <input type="hidden" name="status" value={formData.status} />

            {/* Notes */}
            <Grid item xs={12} sx={{ mt: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={3}
                disabled={isViewMode || isDeleteMode}
                size="small"
                placeholder="Enter any additional notes about this appointment"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>

            {/* Doctor's Schedule Toggle */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                startIcon={<ScheduleIcon />}
                onClick={() => setShowSchedule(!showSchedule)}
                size="small"
                fullWidth
                sx={{
                  mb: 1,
                  justifyContent: 'flex-start',
                  backgroundColor: showSchedule ? 'action.selected' : 'background.paper',
                }}
              >
                {showSchedule ? 'Hide Doctor\'s Schedule' : 'Show Doctor\'s Schedule'}
              </Button>

              <Collapse in={showSchedule}>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  {/* Date Navigation */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <IconButton 
                      size="small" 
                      onClick={() => setSelectedDate(prev => addDays(prev, -1))}
                      disabled={isLoadingSchedule}
                      sx={{ p: 0.5 }}
                    >
                      <ArrowBackIos fontSize="small" />
                    </IconButton>
                    
                    <Typography variant="subtitle2" fontWeight={500}>
                      {format(selectedDate, 'EEEE, MMM d, yyyy')}
                    </Typography>
                    
                    <IconButton 
                      size="small" 
                      onClick={() => setSelectedDate(prev => addDays(prev, 1))}
                      disabled={isLoadingSchedule}
                      sx={{ p: 0.5 }}
                    >
                      <ArrowForwardIos fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {/* Today Button */}
                  <Box textAlign="center" mb={2}>
                    <Button 
                      size="small" 
                      variant="outlined"
                      startIcon={<TodayIcon />}
                      onClick={() => setSelectedDate(new Date())}
                      disabled={isToday(selectedDate) || isLoadingSchedule}
                      sx={{ 
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        py: 0.5
                      }}
                    >
                      Today
                    </Button>
                  </Box>
                  
                  {/* Time Slots */}
                  {isLoadingSchedule ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : doctorSchedule.length > 0 ? (
                    <Grid container spacing={0.5} sx={{ maxHeight: 200, overflowY: 'auto', pb: 0.5, pr: 0.5 }}>
                      {doctorSchedule.map((slot, index) => (
                        <Grid item xs={4} sm={3} md={2} key={index}>
                          <Tooltip 
                            title={getSlotTooltip(slot)} 
                            placement="top"
                            arrow
                          >
                            <Box>
                              <Button
                                fullWidth
                                variant={isTimeSelected(slot.time) ? 'contained' : 'outlined'}
                                color={getSlotColor(slot)}
                                disabled={!slot.available || isViewMode || isDeleteMode || slot.isPast}
                                size="small"
                                onClick={() => handleTimeSlotSelect(slot.time)}
                                sx={{
                                  py: 0.75,
                                  minWidth: 'auto',
                                  fontSize: '0.75rem',
                                  fontWeight: isTimeSelected(slot.time) ? 600 : 500,
                                  textTransform: 'none',
                                  borderRadius: 1,
                                  boxShadow: isTimeSelected(slot.time) ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                                  transition: 'all 0.2s ease-in-out',
                                  borderWidth: isTimeSelected(slot.time) ? 0 : 1,
                                  position: 'relative',
                                  overflow: 'hidden',
                                  ...getSlotStyles(slot, isTimeSelected(slot.time)),
                                  '&:hover': !isViewMode && !isDeleteMode && slot.available && !slot.isPast ? {
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                                  } : {},
                                  '&.Mui-disabled': {
                                    opacity: 0.7,
                                    backgroundColor: slot.isPast 
                                      ? 'rgba(0, 0, 0, 0.02)' 
                                      : !slot.available ? 'rgba(244, 67, 54, 0.08)' : 'transparent',
                                    borderColor: slot.isPast 
                                      ? 'rgba(0, 0, 0, 0.08)' 
                                      : !slot.available ? 'rgba(244, 67, 54, 0.5)' : 'rgba(0, 0, 0, 0.12)',
                                    color: slot.isPast 
                                      ? 'text.disabled' 
                                      : !slot.available ? 'error.main' : 'text.secondary',
                                  },
                                }}
                              >
                                {slot.time}
                                {!slot.available && slot.bookedPatient && (
                                  <Box 
                                    component="span"
                                    sx={{
                                      position: 'absolute',
                                      bottom: 0,
                                      left: 0,
                                      right: 0,
                                      height: '2px',
                                      bgcolor: 'error.main',
                                      opacity: 0.8
                                    }}
                                  />
                                )}
                              </Button>
                              {!slot.available && slot.bookedPatient && (
                                <Typography 
                                  variant="caption" 
                                  component="div"
                                  sx={{
                                    textAlign: 'center',
                                    mt: 0.5,
                                    color: 'error.main',
                                    fontSize: '0.6rem',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {slot.bookedPatient}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid>
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                        No available time slots for this day
                      </Typography>
                    </Grid>
                  )}
                  
                  {/* Legend */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    gap: 2,
                    mt: 1.5,
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    fontSize: '0.7rem',
                    color: 'text.secondary'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        width: 12,
                        height: 12,
                        bgcolor: 'success.light',
                        borderRadius: '3px',
                        border: '1px solid',
                        borderColor: 'success.dark',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }} />
                      <span>Available</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        width: 12,
                        height: 12,
                        bgcolor: 'error.light',
                        border: '1px solid',
                        borderColor: 'error.dark',
                        borderRadius: '3px',
                        opacity: 0.8
                      }} />
                      <span>Booked</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        width: 12,
                        height: 12,
                        bgcolor: 'grey.300',
                        border: '1px solid',
                        borderColor: 'grey.500',
                        borderRadius: '3px',
                        opacity: 0.6
                      }} />
                      <span>Past</span>
                    </Box>
                  </Box>
                </Paper>
              </Collapse>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} color="primary">
              Cancel
            </Button>
            {isDeleteMode ? (
              <Button
                onClick={handleDelete}
                color="error"
                variant="contained"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </Button>
            ) : !isViewMode ? (
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting || isBooking}
                startIcon={(isSubmitting || isBooking) ? <CircularProgress size={20} /> : null}
              >
                {(isSubmitting || isBooking) 
                  ? (formData.isNonRegisteredPatient ? 'Processing...' : 'Saving...') 
                  : (formData.isNonRegisteredPatient ? 'Save & Book' : 'Save')}
              </Button>
            ) : null}
          </DialogActions>
        </form>

        {/* Duplicate Phone Number Dialog with 3 Options */}
        <Dialog
          open={duplicateDialog.open}
          onClose={() => setDuplicateDialog({ open: false })}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            Phone Number Already Exists
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              A patient with phone number <strong>{duplicateDialog.existingPatient?.phoneNumber}</strong> already exists:
            </DialogContentText>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {duplicateDialog.existingPatient?.fullName}
              </Typography>
              <Typography variant="body2">
                <strong>MRN:</strong> {duplicateDialog.existingPatient?.mrn}
              </Typography>
              <Typography variant="body2">
                <strong>Age:</strong> {duplicateDialog.existingPatient?.age || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Gender:</strong> {duplicateDialog.existingPatient?.gender || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {duplicateDialog.existingPatient?.email || 'N/A'}
              </Typography>
            </Box>
            <DialogContentText sx={{ mt: 2 }}>
              What would you like to do?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, alignItems: 'stretch', p: 2 }}>
            <Button 
              onClick={handleBookForExistingPatient}
              variant="contained"
              color="primary"
              fullWidth
            >
              Book Appointment for Existing Patient
            </Button>
            <Button 
              onClick={handleLinkAsFamily}
              variant="outlined"
              color="secondary"
              fullWidth
              disabled={!nonRegisteredPatientInfo.fullName.trim()}
            >
              Link as Family Member ({nonRegisteredPatientInfo.fullName || 'Enter name first'})
            </Button>
            <Button 
              onClick={handleUseDifferentNumber}
              variant="text"
              color="inherit"
              fullWidth
            >
              Use Different Phone Number
            </Button>
          </DialogActions>
        </Dialog>

        {/* Name Conflict Dialog */}
        <Dialog
          open={nameConflictDialog.open}
          onClose={() => setNameConflictDialog({ open: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Name Already Exists in Family
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              The name <strong>"{nameConflictDialog.conflictName}"</strong> already exists within this family. 
              Each family member must have a unique name.
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              Please choose a different name or add a middle name/initial to make it unique.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setNameConflictDialog({ open: false })}
              variant="contained"
              color="primary"
            >
              OK, I'll Change the Name
            </Button>
          </DialogActions>
        </Dialog>
    </Dialog>
  );
};

export default AppointmentDialog;
